// Billing Service API Types

// === ENUMS ===

export enum TransactionEventType {
  Deposit = 1, // Money deposit into account
  EventCharge = 2, // Charge for event participation
  SelfCharge = 3, // Manual charge/withdrawal
}

export enum TransactionType {
  Debit = 1, // Money going out of account
  Credit = 2, // Money coming into account
}

export enum AccountType {
  Main = 1, // Main account balance
  Pending = 2, // Pending transactions account
}

// === CORE ENTITIES ===

export interface Payment {
  typeId: TransactionEventType;
  externalId: string | null;
  createdAt: string;
  transactions: Transaction[];
}

export interface Transaction {
  id: string;
  typeId: TransactionType;
  eventId: string;
  accountId: string;
  amount: string; // Amount as string for precision
  description: string | null;
  createdAt: string;
  isRolledBack?: boolean; // Whether the transaction was rolled back (cancelled)
}

export interface Account {
  id: string;
  userId: string;
  typeId: AccountType; // Account type (Main or Pending)
  balance: string; // Balance as string for precision
}

export interface PendingBalance {
  userId: string;
  balance: string; // Pending balance as string for precision
}

export interface PaymentsCursor {
  createdAt: string; // Timestamp for pagination
  id: string; // ID for pagination
}

// === REQUEST/RESPONSE TYPES ===

// Get Payments
export interface GetPaymentsParams {
  typeId?: TransactionEventType; // Optional: Type of transaction event (if not provided, returns all types)
  userId?: string; // Optional: User ID (admin only)
  cursor?: PaymentsCursor; // Optional: Pagination cursor
  limit: number; // Required: Pagination limit (max 100)
}

export interface GetPaymentsResponse {
  payments: Payment[];
  cursor: PaymentsCursor | null;
}

// Create Deposit
export interface CreateDepositRequest {
  userId?: string; // Optional: User ID (admin only)
  amount: number; // Required: Deposit amount
  comment?: string; // Optional: Comment about the deposit (max 300 chars)
}

// Approve Deposit
export interface ApproveDepositParams {
  eventId: string; // Required: Transaction event ID to approve
}

// Cancel Deposit
export interface CancelDepositParams {
  eventId: string; // Required: Transaction event ID to cancel
}

// Create Charge
export interface CreateChargeRequest {
  userId?: string; // Optional: User ID (admin only)
  amount: number; // Required: Charge amount
  comment: string; // Required: Comment explaining the charge (max 300 chars)
}

// Get User Accounts
export interface GetUserAccountsParams {
  userId?: string; // Optional: User ID (admin only)
}

export interface UserAccountData {
  userId: string;
  accounts: Account[];
  pendingBalance: string; // Pending balance as string
}

export interface GetUserAccountsResponse {
  data: UserAccountData[];
}

// === UTILITY TYPES ===

export interface ParsedExternalId {
  instanceId: string;
  userId: string;
}
